import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="glass-card rounded-xl border border-white/5 bg-[#14161f] overflow-hidden transition-colors hover:border-white/10"
    >
      <button 
        onClick={onClick}
        className="w-full flex items-center justify-between p-6 text-left cursor-pointer focus:outline-none"
      >
        <h3 className="text-[15px] font-bold text-white pr-8">{question}</h3>
        <div className="flex-shrink-0 text-slate-400 mt-1">
          <SafeIcon icon={isOpen ? FiIcons.FiChevronUp : FiIcons.FiChevronDown} className="text-lg" />
        </div>
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <div className="px-6 pb-6 text-sm text-slate-300 leading-relaxed font-medium">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqData = [
    {
      question: "Do I need my own YouTube channel?",
      answer: "No. We publish on our owned channels. You still get the traffic and brand exposure through the video content without the overhead of managing a channel."
    },
    {
      question: "How long do videos keep bringing traffic?",
      answer: "These are built as lifetime assets. Many videos continue generating views and clicks for months or years, depending on the keyword and niche competitiveness."
    },
    {
      question: "Can this work in any niche?",
      answer: "Yes. We focus on keywords and formats that people actively search. We've worked across agencies, SaaS, ecom, local services, crypto, forex, AI, and more."
    },
    {
      question: "Do you do YouTube and Google, or only one?",
      answer: "Both. You can choose YouTube-only, Google-only, or a combined plan depending on where your specific audience search behavior is highest."
    },
    {
      question: "How fast do results usually appear?",
      answer: "Most videos index within 24-48 hours on Google. Rankings typically stabilize within 1-2 weeks depending on keyword difficulty and your niche's competition level."
    },
    {
      question: "What if Google does not show video results for my keyword?",
      answer: "We perform deep research to target keywords that already trigger video carousels or have a high probability of appearing in Google search results based on intent."
    }
  ];

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="relative py-24 bg-[#0B0F19] overflow-hidden border-b border-white/5">
      {/* Subtle bottom glow matching the screenshot */}
      <div className="absolute bottom-0 left-1/2 w-full max-w-2xl h-[300px] bg-fuchsia-900/10 blur-[150px] rounded-full pointer-events-none -translate-x-1/2 translate-y-1/2"></div>
      
      <div className="max-w-3xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold text-white tracking-tight"
          >
            Frequently Asked Questions
          </motion.h2>
        </div>

        <div className="space-y-4">
          {faqData.map((faq, index) => (
            <FAQItem 
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onClick={() => handleToggle(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;